import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FaQuoteLeft, FaStar } from "react-icons/fa";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const testimonials = [
  {
    nameKey: "testimonials.amina.name",
    roleKey: "testimonials.amina.role",
    quoteKey: "testimonials.amina.quote",
    avatar: "/assets/images/emily.png",
    rating: 5
  },
  {
    nameKey: "testimonials.tarek.name",
    roleKey: "testimonials.tarek.role",
    quoteKey: "testimonials.tarek.quote",
    avatar: "/assets/images/mohamed.png",
    rating: 4
  },
  {
    nameKey: "testimonials.lina.name",
    roleKey: "testimonials.lina.role",
    quoteKey: "testimonials.lina.quote",
    avatar: "/assets/images/sarah.png",
    rating: 5
  }
];

const TestimonialCard = ({ testimonial, i }) => {
  const { t } = useTranslation();

  return (
    <motion.div
      key={i}
      className="bg-white rounded-xl shadow-lg p-6 text-left hover:shadow-xl transition-all duration-300 relative flex flex-col h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: i * 0.15 }}
      viewport={{ once: true }}
    >
      <div className="flex items-center mb-4 gap-3">
        <img
          src={testimonial.avatar}
          alt={t(testimonial.nameKey)}
          className="w-12 h-12 rounded-full object-cover border border-gray-200"
        />
        <div>
          <h4 className="font-semibold text-gray-900 text-sm">{t(testimonial.nameKey)}</h4>
          <span className="text-xs text-gray-500">{t(testimonial.roleKey)}</span>
        </div>
      </div>

      <FaQuoteLeft className="text-primary mb-3" size={20} />
      <p className="text-gray-700 text-base leading-relaxed italic mb-4 flex-1">
        {t(testimonial.quoteKey)}
      </p>

      <div className="flex space-x-1 mt-auto">
        {Array.from({ length: testimonial.rating }).map((_, idx) => (
          <FaStar key={idx} className="text-yellow-400" size={14} />
        ))}
      </div>
    </motion.div>
  );
};

const TestimonialSection = () => {
  const { t } = useTranslation();

  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    arrows: false,
    slidesToShow: 1.1,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1.2
        }
      }
    ]
  };

  return (
    <section className="bg-gray-100 py-20 px-6">
      <div className="max-w-6xl mx-auto text-center">
        <motion.h2
          className="text-3xl font-bold text-gray-900 mb-12"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          {t("testimonials.title")}
        </motion.h2>

        {/* Desktop Grid View */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, i) => (
            <TestimonialCard key={i} testimonial={testimonial} i={i} />
          ))}
        </div>

        {/* Mobile Carousel View */}
        <div className="md:hidden">
          <Slider {...settings}>
            {testimonials.map((testimonial, i) => (
              <div key={i} className="pr-4">
                <TestimonialCard testimonial={testimonial} i={i} />
              </div>
            ))}
          </Slider>
        </div>

        {/* CTA Block */}
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold text-gray-800 mb-1">
            {t("testimonials.ctaTitle")}
          </h3>
          <a
            href="/use-cases"
            className="inline-block border border-gray-400 text-gray-800 px-6 mt-8 py-3 rounded-full font-medium hover:bg-gray-100 transition"
          >
            {t("testimonials.ctaButton")}
          </a>
        </div>
      </div>
    </section>
  );
};

export default TestimonialSection;
